import type { Metadata, PartialMetadataMap } from '@xing-com/crate-core-assets';
import { joinUrl } from '@xing-com/crate-core-assets';
import type { Contributes, Site } from '@xing-com/crate-xinglet';

import { SITES } from './sites';

export type RawRouteConfig = Exclude<Contributes['routes'], undefined>[number];

export type RouteConfig = Exclude<RawRouteConfig, string> & { owner: string };

export type RouteInfo = {
  plug: string;
  path: string;
  catchAll?: string;
  route: string;
  redirectTo?: string;
  outlet?: string;
  owner: string;
  xinglet: string;
  config: Contributes;
};

function extractRouteConfigs(metadata: Partial<Metadata>): RouteConfig[] {
  const { contributes = {}, owner = 'unknown' } = metadata;
  const { routes } = contributes;

  return (routes ?? []).map((route) => {
    return 'redirectTo' in route
      ? { ...route, owner }
      : { ...contributes, ...route, owner };
  });
}

export function extractRouteInfos(
  metadataMap: PartialMetadataMap
): RouteInfo[] {
  const regExp = /^(?:@([\w-]+))?([/\w-:]*?)(?:\/@([\w-]+)|(?:\/\*([\w-]+)))?$/;

  const infos = Object.entries(metadataMap).flatMap(([xinglet, metadata]) => {
    const configs = extractRouteConfigs(metadata);

    return configs.map((config): RouteInfo => {
      const {
        path: route,
        redirectTo,
        owner,
        ...rest
      } = { redirectTo: undefined, ...config };

      const matchData = route.match(regExp);
      if (!matchData) throw new Error(`Invalid route: ${route}`);
      const [, plug = 'xing', path, outlet, splat] = matchData;

      return {
        plug,
        path: path.startsWith('/') ? path : `/${path}`,
        catchAll: splat,
        route,
        redirectTo,
        outlet,
        owner,
        xinglet,
        config: rest,
      };
    });
  });

  return infos;
}

type RouteNode = { info: RouteInfo; nodes: RouteNode[] };

function buildRoutesGraph(
  metadataMap: PartialMetadataMap
): Map<string, RouteNode[]> {
  const infos = extractRouteInfos(metadataMap);
  const outletToPlugsMap = new Map<string, RouteNode[]>();

  const getOrInsert = (outlet: string): RouteNode[] => {
    const plugs = outletToPlugsMap.get(outlet) ?? [];
    outletToPlugsMap.set(outlet, plugs);
    return plugs;
  };

  for (const info of infos) {
    const nodes = info.outlet ? getOrInsert(info.outlet) : [];
    getOrInsert(info.plug).push({ info, nodes });
  }

  return outletToPlugsMap;
}

export type Route = {
  site: Site;
  path: string;
  catchAll?: string;
  route: string;
  redirectTo?: string;
  owner: string;
  xinglet: string;
  parents: Route[];
  config: Contributes;
};

function extractRoutesFromGraph(parent: Route, nodes: RouteNode[]): Route[] {
  if (nodes.length === 0) return [parent];

  return nodes.flatMap(({ info, nodes: children }) => {
    const { redirectTo, owner, xinglet, config, catchAll } = info;
    const parents = [...parent.parents, parent];
    const parentRoutes = parents.map(({ route }) => route);

    if (parentRoutes.includes(info.route)) {
      throw new Error(
        `Circular outlets detected in routes: ${parentRoutes.join(' -> ')} -> ${info.route}`
      );
    }

    const path =
      info.path === '/' ? parent.path : joinUrl(parent.path, info.path);
    const site = parent.site;
    const route: Route = {
      site,
      path,
      catchAll,
      route: info.route,
      redirectTo,
      owner,
      xinglet,
      parents,
      config: Object.assign({}, ...parents.map(({ config }) => config), config),
    };

    return extractRoutesFromGraph(route, children);
  });
}

function sortRoutes(routes: Route[]): Route[] {
  // A negative value indicates that a should come before b.
  // A positive value indicates that a should come after b.
  return routes.toSorted((a, b) => {
    const partsA = a.path.split('/').slice(1);
    const partsB = b.path.split('/').slice(1);
    const max = Math.max(partsA.length, partsB.length);

    for (let i = 0; i < max; i++) {
      const partA = partsA[i];
      const partB = partsB[i];

      if (partA === partB) continue;

      // `undefined` comes after not `undefined`
      if (partA === undefined && partB !== undefined) return +1;
      if (partA !== undefined && partB === undefined) return -1;

      // '' comes after not ''
      if (partA === '' && partB !== '') return +1;
      if (partA !== '' && partB === '') return -1;

      const charA = partA[0];
      const charB = partB[0];

      // ':' comes after not ':'
      if (charA === ':' && charB !== ':') return +1;
      if (charA !== ':' && charB === ':') return -1;

      return partA.localeCompare(partB);
    }

    // Splat routes come after non-splat routes because they also match the non-splat routes.
    if (a.catchAll && !b.catchAll) return +1;
    if (!a.catchAll && b.catchAll) return -1;

    return a.path.localeCompare(b.path);
  });
}

export function extractRoutes(
  metadataMap: PartialMetadataMap,
  filterBySite?: string
): Route[] {
  const nodes = buildRoutesGraph(metadataMap);

  const routes = SITES.flatMap((site) => {
    if (filterBySite && site !== filterBySite) return [];
    const siteNodes = nodes.get(site) ?? [];
    return siteNodes.flatMap(({ info, nodes: children }) => {
      const { plug: _plug, outlet: _outlet, ...rest } = info;
      return extractRoutesFromGraph({ site, parents: [], ...rest }, children);
    });
  });

  return sortRoutes(routes);
}
